interface Invocation {
    timestamp: number;
}

interface InvocationStats {
    avg10min: number;
    avg5min: number;
    avg1min: number;
    max: number;
}

// Store invocations for each function
export const invocations: Record<string, Invocation[]> = {};

// Cache for storing max invocation counts in 10-second windows
export const maxInvocationsCache: Record<string, number> = {};

/**
 * Records a function invocation
 * @param name Unique identifier for the function
 */
export function recordInvocation(name: string): void {
    const timestamp = Date.now();

    if (!invocations[name]) {
        invocations[name] = [];
        maxInvocationsCache[name] = 0;
    }

    invocations[name].push({ timestamp });

    // Clean up old invocations (older than 10 minutes)
    const tenMinutesAgo = timestamp - 10 * 60 * 1000;
    invocations[name] = invocations[name].filter(inv => inv.timestamp >= tenMinutesAgo);

    // Update max invocations in a 10-second window
    updateMaxInvocations(name);
}

/**
 * Updates the maximum invocation count in a 10-second window for the specified function
 */
function updateMaxInvocations(name: string): void {
    const now = Date.now();
    const tenSecondsAgo = now - 10 * 1000;

    // Count invocations in the last 10 seconds
    const recentInvocations = invocations[name].filter(inv => inv.timestamp >= tenSecondsAgo);
    const count = recentInvocations.length;

    // Update max if current count is higher
    if (count > maxInvocationsCache[name]) {
        maxInvocationsCache[name] = count;
    }
}

/**
 * Gets statistics about function invocations
 * @param name Unique identifier for the function
 * @returns Object containing average invocations per second and maximum in 10-second window
 */
export function getInvocationStats(name: string): InvocationStats {
    if (!invocations[name]) {
        return {
            avg10min: 0,
            avg5min: 0,
            avg1min: 0,
            max: 0,
        };
    }

    const now = Date.now();
    const oneMinuteAgo = now - 60 * 1000;
    const fiveMinutesAgo = now - 5 * 60 * 1000;
    const tenMinutesAgo = now - 10 * 60 * 1000;

    const invocationsLast1Min = invocations[name].filter(inv => inv.timestamp >= oneMinuteAgo).length;
    const invocationsLast5Min = invocations[name].filter(inv => inv.timestamp >= fiveMinutesAgo).length;
    const invocationsLast10Min = invocations[name].filter(inv => inv.timestamp >= tenMinutesAgo).length;

    // Calculate averages per second
    const avg1min = invocationsLast1Min / 60;
    const avg5min = invocationsLast5Min / (5 * 60);
    const avg10min = invocationsLast10Min / (10 * 60);

    return {
        avg10min,
        avg5min,
        avg1min,
        max: maxInvocationsCache[name],
    };
}
